// src/types/chat.ts

// ---- Actions you send ----
export const ChatWsMessageAction = {
  SEND_MESSAGE: "send_message",
  SEND_AUDIO: "send_audio",
} as const;
export type ChatWsMessageAction =
  (typeof ChatWsMessageAction)[keyof typeof ChatWsMessageAction];

export const ChatWsAudioAction = {
  GET_AUDIO: "get_audio",
} as const;
export type ChatWsAudioAction =
  (typeof ChatWsAudioAction)[keyof typeof ChatWsAudioAction];

export const ChatWsVideoAction = {
  GET_VIDEO: "get_video",
} as const;
export type ChatWsVideoAction =
  (typeof ChatWsVideoAction)[keyof typeof ChatWsVideoAction];

// ---- Chunk types you receive (and sometimes send) ----
export const ChatWsChunkType = {
  USER_MESSAGE: "user_message",
  USER_VOICE_MESSAGE: "user_voice_message",
  AI_RESPONSE: "ai_response",
  AI_GREETING: "ai_response",
  TYPING_START: "typing_start",
  TYPING_END: "typing_end",
  GET_VOICE_ID: "get_voice_id",
  GET_VIDEO_ID: "get_video_id",
  ERROR_WARNING: "ERROR_WARNING",
} as const;
export type ChatWsChunkType =
  (typeof ChatWsChunkType)[keyof typeof ChatWsChunkType];

export const AdditionalTypes = {
  AI_GREETING: "ai_greeting",
} as const;
export type AdditionalTypes =
  (typeof AdditionalTypes)[keyof typeof AdditionalTypes];

// ---- Outbound payloads (client -> server) ----
export interface ChatWsMessageIn {
  action: ChatWsMessageAction;
  content?: string;
  output_language?: string | null;
  custom_id?: string | null;
  // only when SEND_AUDIO
  file_base64?: string;
}

export interface ChatWsAudioOrVideoIn {
  action: ChatWsAudioAction | ChatWsVideoAction;
  chat_message_id: string;
  output_language?: string | null;
  custom_id?: string | null;
}

// ---- Inbound payloads (server -> client) ----
export interface ChatWsMessageOut {
  id: string;
  chat_session_id: string;
  user_id: string;
  is_human: boolean;
  content: string;
  created_at?: string;
  type: ChatWsChunkType; // AI_RESPONSE / USER_MESSAGE / USER_VOICE_MESSAGE
  additional_type?: AdditionalTypes;
  chat_message_id?: string;
  custom_id?: string;
}

export interface ChatWsTypingOut {
  type: typeof ChatWsChunkType.TYPING_START | typeof ChatWsChunkType.TYPING_END;
  user_id: string;
}

export interface ChatWsOut {
  type:
    | typeof ChatWsChunkType.GET_VOICE_ID
    | typeof ChatWsChunkType.GET_VIDEO_ID;
  content: string; // file_id
  chat_message_id: string;
  custom_id?: string;
}

export const ErrorType = {
  ERROR: "error",
  WARNING: "warning",
} as const;
export type ErrorType = (typeof ErrorType)[keyof typeof ErrorType];
export interface WsErrorWarningResponse {
  error_type: ErrorType;
  error_code: string; // WarningCodeType | ErrorCodeType
  data: unknown;
}

// Union of messages the client can receive
export type ServerEvent =
  | ChatWsTypingOut
  | ChatWsMessageOut
  | ChatWsOut
  | WsErrorWarningResponse;

export interface User {
  id: string;
  currentProfileImage: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  subscriptionId: string;
  subscriptionName: string;
  twinPhoneNumber: string;
  twinPhoneNumberFriendlyForm: string;
  holloLink: string;
  createdAt: string;
  updatedAt: string;
  isLinkPageDone: boolean;
  livePageEnabled: boolean;
  botIdleFileId: string | null;
  bio: string;
  hasPoweredBy: boolean;
  logoId: string;
  isEmbedEnabled: boolean;
  embedStyle?: "default" | "gradient_minimalist";
}

export type UiMessage = Omit<Partial<ChatWsMessageOut>, "created_at"> & {
  id: string;
  is_human: boolean;
  content?: string;
  created_at?: number | string;
  audioUrl?: string;
  mimeType?: string;
  custom_id?: string;
  _optimistic?: boolean;
  autoplay?: boolean;
  loadingVoice?: boolean;
  loadingVideo?: boolean;
  for_message_id?: string;
  chat_message_id?: string;
};
